import { formatStoreDisplayFromPriceFromCents } from '@/lib/store/storeDisplayPricing'
import { capitalizeFirstLetter } from '@/lib/utilities'
import type {
    DirectoryPharmacyDetail,
    DirectoryPharmacyMedicationsPage,
    DirectoryPharmacyMedicationsResponse,
    PharmacyDetailPageApiResponse,
    PharmacyDetailPageMedicationApi,
    PharmacyDetailPageMedicationsApiResponse,
    PharmacyMedicationOffering,
} from '@/lib/store/api/directoryPharmacy/types'
import type { GoogleHours } from '@/types/interfaces/pharmacy'

function mapGoogleHours(
    googleHours: PharmacyDetailPageApiResponse['google_hours'],
): GoogleHours | null {
    if (googleHours == null || Object.keys(googleHours).length === 0) return null
    return googleHours as GoogleHours
}

export const PHARMACY_MEDICATIONS_PAGE_SIZE = 50

export function hyphenatePharmacyName(name: string): string {
    return name
        .trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
}

export function mapPharmacyMedicationOffering(
    medication: PharmacyDetailPageMedicationApi,
): PharmacyMedicationOffering {
    return {
        skuId: String(medication.sku_id),
        name: capitalizeFirstLetter(medication.display_name),
        imageUrl: medication.image_url,
        productDetailPageSlug: medication.product_detail_page_slug,
        priceLabel:
            medication.total_price_cents != null
                ? formatStoreDisplayFromPriceFromCents(medication.total_price_cents)
                : null,
    }
}

export function mapPharmacyMedicationsPage(
    response: PharmacyDetailPageMedicationsApiResponse,
): DirectoryPharmacyMedicationsPage {
    const page = response.meta?.page ?? 1
    const totalPages = response.meta?.total_pages ?? 0

    return {
        items: response.medications?.map(mapPharmacyMedicationOffering) ?? [],
        page,
        totalPages,
        hasMore: page < totalPages,
    }
}

export function mapPharmacyMedicationsResponse(
    response: PharmacyDetailPageMedicationsApiResponse,
): DirectoryPharmacyMedicationsResponse {
    const page = mapPharmacyMedicationsPage(response)
    return {
        items: page.items,
        page: page.page,
        totalPages: page.totalPages,
        hasMore: page.hasMore,
    }
}

export function mapDirectoryPharmacyDetail(
    info: PharmacyDetailPageApiResponse,
    medications: PharmacyDetailPageMedicationsApiResponse,
): DirectoryPharmacyDetail {
    return {
        friendlyId: String(info.friendly_id),
        ncpdpProviderId: info.ncpdp_provider_id,
        npi: info.npi,
        name: info.name,
        hyphenatedName: hyphenatePharmacyName(info.name),
        pharmacyType: info.is_mail_order ? 'Home delivery pharmacy' : 'Retail pharmacy',
        logoUrl: info.logo_url,
        googleRating: info.google_rating,
        googleRatingCount: info.google_rating_count,
        address: {
            line1: info.address_line1,
            city: info.city,
            state: info.state_code,
            zip: info.zip_code,
        },
        latitude: info.latitude,
        longitude: info.longitude,
        distanceMiles: info.distance_miles,
        phone: info.phone,
        websiteUrl: info.website,
        hoursOfOperation: info.hours_of_operation,
        open24Hours: info.open_24_hours || false,
        googleHours: mapGoogleHours(info.google_hours),
        medications: mapPharmacyMedicationsPage(medications),
    }
}

export function emptyPharmacyMedicationsApiResponse(): PharmacyDetailPageMedicationsApiResponse {
    return {
        medications: [],
        meta: {
            page: 1,
            per_page: PHARMACY_MEDICATIONS_PAGE_SIZE,
            total_count: 0,
            total_pages: 0,
        },
    }
}
